import { ThemeProvider } from '@emotion/react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {Route, Routes, useMatch, useLocation, useNavigate} from 'react-router-dom';
import parse from 'url-parse';
import defaultTheme from 'shared/defaultTheme';
import { ToolLoader } from 'shared/components';
import { INIT, NAVIGATE, UPDATE } from 'shared/wizard/actions';
import ToolContainer from 'shared/wizard/ToolContainer';
import { Flow } from './components';
import { flowTypes } from './config';
import getSettings from './helpers';
import { lazyWithRetry, useTrackPageview } from "shared/utility";

const applicantFormPromise = import(
  /* webpackChunkName: "orv-applicant-form " */
  './components/ApplicantForm'
);
const partnerFormPromise = import(
  /* webpackChunkName: "orv-partner-form" */
  './components/PartnerForm'
);
const newOrvPromise = import(
  /* webpackChunkName: "orv-new-orv" */
  './components/NewOrv'
);
const existingOrvPromise = import(
  /* webpackChunkName: "orv-existing-orv" */
  './components/ExistingOrv'
);
const wizardFinalPromise = import(
  /* webpackChunkName: "orv-wizard-final" */
  './components/WizardFinal'
);
const outputFinalPromise = import(
  /* webpackChunkName: "orv-output-final " */
  './components/OutputFinal'
);

const OutputFinal = lazyWithRetry(() => outputFinalPromise);
const ApplicantForm = lazyWithRetry(() => applicantFormPromise);
const PartnerForm = lazyWithRetry(() => partnerFormPromise);
const NewOrv = lazyWithRetry(() => newOrvPromise);
const ExistingOrv = lazyWithRetry(() => existingOrvPromise);
const WizardFinal = lazyWithRetry(() => wizardFinalPromise);

const App = () => {
  const { app, framed, settings, pages, input } = useSelector(state => state);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState(defaultTheme);
  const base = `${app}${framed ? '/framed' : ''}`;
  const overrideMatch = useMatch(`/${base}/flow`);

  useTrackPageview();

  useEffect(() => {
    navigate(`/${app}${framed ? '/framed' : ''}/${pages[0] + location.search}`, {
      state: { page: pages[0] },
      replace: true,
    });
  }, [framed]);

  useEffect(() => {
    const { flow } = parse(location.search, true).query;
    if (flow && Object.keys(flowTypes).includes(flow)) {
      const newFlow = flowTypes[flow];
      const newPages = pages.filter(page => page !== 'flow');
      dispatch({ type: UPDATE, payload: { pages: newPages, input: { ...input, flow: newFlow } } });
      dispatch({ type: NAVIGATE, location, navigate, delta: 0 });
    }

    getSettings(res => {
      dispatch({ type: INIT, payload: { settings: res.data } });
      setTheme(theme => ({ ...theme, ...res.data }));
    });
  }, []);

  const outputRoute = (
    <Routes>
      <Route path={`/${base}/final`} element={<OutputFinal />} />
    </Routes>
  );

  const inputRouteSwitch = (
    <Routes>
      <Route path={`/${base}/applicant`} element={<ApplicantForm />} />
      <Route path={`/${base}/partner`} element={<PartnerForm />} />
      <Route path={`/${base}/coverage`} element={input.flow === flowTypes.new ? <NewOrv /> : <ExistingOrv />} />
      <Route path={`/${base}/final`} element={<WizardFinal />} />
    </Routes>
  );

  if (!settings) return <ToolLoader show />;
  return (
    <ThemeProvider theme={theme}>
      <ToolContainer Input={inputRouteSwitch} Output={outputRoute} override={overrideMatch && <Flow />} />
    </ThemeProvider>
  );
};

export default App;
